import {
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { HttpAgent } from 'src/api/agent';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';

// Enums based on RegistryEntity
enum RegistryValueType {
  STRING = 'string',
  NUMBER = 'number',
  CODEABLECONCEPT = 'codeableConcept',
}

enum RegistryInputMode {
  MANUAL = 'manual',
  SELECTION = 'selection',
}

interface RegistryEntry {
  uuid?: string;
  id?: number;
  keyRegistry: string;
  keyValue: string;
  groupIdentifier: string;
  inputMode: RegistryInputMode | '';
  valueType: RegistryValueType | '';
  keyDescription: string;
}

const registryInitialState: RegistryEntry = {
  keyRegistry: '',
  keyValue: '',
  groupIdentifier: '',
  inputMode: '',
  valueType: '',
  keyDescription: '',
};

export default function RegistryList() {
  const [registries, setRegistries] = useState<RegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [registryInForm, setRegistryInForm] = useState<RegistryEntry>(registryInitialState);
  const [editingRegistry, setEditingRegistry] = useState<RegistryEntry | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRegistry, setSelectedRegistry] = useState<RegistryEntry | null>(null);

  const fetchRegistries = useCallback(async () => {
    try {
      setLoading(true);
      // Passing empty object as we want all registries initially
      const response = await HttpAgent.Registry.getRegistries({});
      setRegistries(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch registries', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistries();
  }, [fetchRegistries]);

  const handleOpen = () => {
    setEditingRegistry(null);
    setRegistryInForm(registryInitialState);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setRegistryInForm(registryInitialState);
    setEditingRegistry(null);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>, registry: RegistryEntry) => {
    setAnchorEl(event.currentTarget);
    setSelectedRegistry(registry);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRegistry(null);
  };

  const handleEditClick = () => {
    if (selectedRegistry) {
      setEditingRegistry(selectedRegistry);
      setRegistryInForm(selectedRegistry);
      setOpen(true);
    }
    handleMenuClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegistryInForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveRegistry = async () => {
    try {
      if (editingRegistry && editingRegistry.uuid) {
        await HttpAgent.Registry.update(editingRegistry.uuid, registryInForm);
      } else {
        await HttpAgent.Registry.create(registryInForm);
      }
      handleClose();
      fetchRegistries();
    } catch (error) {
      console.error('Failed to save registry entry', error);
    }
  };

  return (
    <Page title="Registries">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Registry List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Registries' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={handleOpen}
            >
              New Registry
            </Button>
          }
        />

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key Registry</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Value Type</TableCell>
                  <TableCell>Input Mode</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {registries.map((row) => (
                  <TableRow key={row.uuid || row.id}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{row.keyRegistry}</TableCell>
                    <TableCell>{row.groupIdentifier || '-'}</TableCell>
                    <TableCell>{row.valueType}</TableCell>
                    <TableCell>{row.inputMode}</TableCell>
                    <TableCell>{row.keyDescription}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
        </Menu>

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>{editingRegistry ? 'Edit Registry' : 'New Registry'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="keyRegistry"
                label="Registry Key"
                value={registryInForm.keyRegistry}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g. observation_types"
              />
              <TextField
                name="groupIdentifier"
                label="Group Identifier"
                value={registryInForm.groupIdentifier}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="valueType"
                select
                label="Value Type"
                value={registryInForm.valueType}
                onChange={handleInputChange}
                fullWidth
              >
                {Object.values(RegistryValueType).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="inputMode"
                select
                label="Input Mode"
                value={registryInForm.inputMode}
                onChange={handleInputChange}
                fullWidth
              >
                {Object.values(RegistryInputMode).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="keyValue"
                label="Key Value"
                multiline
                rows={4}
                value={registryInForm.keyValue}
                onChange={handleInputChange}
                fullWidth
                placeholder="Enter JSON or plain text value"
              />
              <TextField
                name="keyDescription"
                label="Description"
                value={registryInForm.keyDescription}
                onChange={handleInputChange}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleSaveRegistry}
              variant="contained"
              disabled={!registryInForm.keyRegistry || !registryInForm.keyValue}
            >
              {editingRegistry ? 'Save Changes' : 'Create Registry'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}